import { Loader2 } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { useGetOrganizationSettings } from "~/api/organizations/organizations";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { useProjectParams } from "~/helpers";
import type {
  FolderWithBates,
  OrganizationSettingsWithBates,
} from "~/types/bates";
import {
  BATES_DEFAULT_DIGITS,
  BATES_DEFAULT_PREFIX_SOURCE,
  BATES_DEFAULT_START_NUMBER,
  BATES_DEFAULT_SUFFIX_SOURCE,
  isBatesPrefixSource,
  isBatesSuffixSource,
  resolveBatesPreview,
} from "~/types/bates";
import { useParentCase } from "../../hooks/useParentCase";

interface ApplyBatesConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentCount: number;
  onConfirm: () => Promise<void> | void;
}

/**
 * Confirmation gate shown before stamping Bates numbers. Stamping is
 * irreversible, so we surface the document count and a preview of the exact
 * nomenclature (derived from org settings + the case prefix override) before
 * the user commits. The preview uses the same resolver as the org settings
 * screen and the case mini-settings so it stays consistent everywhere.
 */
export const ApplyBatesConfirmDialog: FC<ApplyBatesConfirmDialogProps> = ({
  open,
  onOpenChange,
  documentCount,
  onConfirm,
}) => {
  const intl = useIntl();
  const { organizationId } = useProjectParams();
  const { data: settings } = useGetOrganizationSettings(organizationId);
  const { project: masterCase } = useParentCase();
  const [isConfirming, setIsConfirming] = useState(false);

  const settingsWithBates = settings as
    | OrganizationSettingsWithBates
    | undefined;
  const caseWithBates = masterCase as FolderWithBates | undefined;

  const orgPrefixSource = isBatesPrefixSource(
    settingsWithBates?.batesPrefixSource
  )
    ? settingsWithBates.batesPrefixSource
    : BATES_DEFAULT_PREFIX_SOURCE;
  const orgSuffixSource = isBatesSuffixSource(
    settingsWithBates?.batesSuffixSource
  )
    ? settingsWithBates.batesSuffixSource
    : BATES_DEFAULT_SUFFIX_SOURCE;

  const previewText = resolveBatesPreview({
    prefixSource: orgPrefixSource,
    customPrefix: settingsWithBates?.batesPrefix ?? "",
    suffixSource: orgSuffixSource,
    customSuffix: settingsWithBates?.batesSuffix ?? "",
    explicitPrefix: caseWithBates?.batesPrefix ?? "",
    startNumber:
      settingsWithBates?.batesStartNumber ?? BATES_DEFAULT_START_NUMBER,
    digits: settingsWithBates?.batesDigits ?? BATES_DEFAULT_DIGITS,
    placeholders: {
      caseName: intl.formatMessage({
        id: "settings.batesNumbering.previewCaseNamePrefix",
        defaultMessage: "CaseName",
      }),
      fileName: intl.formatMessage({
        id: "settings.batesNumbering.previewFileNamePrefix",
        defaultMessage: "FileName",
      }),
    },
  }).text;

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <FormattedMessage
              id="bates.applyConfirm.title"
              defaultMessage="Apply Reference Numbers?"
            />
          </AlertDialogTitle>
          <AlertDialogDescription>
            <FormattedMessage
              id="bates.applyConfirm.description"
              defaultMessage="This will stamp reference numbers onto {count, plural, one {# document} other {# documents}}. Stamping is permanent and can't be undone."
              values={{ count: documentCount }}
            />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-3 py-2">
          <span className="text-xs font-medium text-muted-foreground">
            <FormattedMessage
              id="bates.applyConfirm.previewLabel"
              defaultMessage="Preview"
            />
          </span>
          <span
            className="font-mono text-sm text-foreground"
            data-testid="applyBatesPreview"
          >
            {previewText}
          </span>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => onOpenChange(false)}
            disabled={isConfirming}
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </AlertDialogCancel>
          <Button
            onClick={handleConfirm}
            disabled={isConfirming}
            aria-busy={isConfirming}
          >
            {isConfirming && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <FormattedMessage
              id="bates.applyBates"
              defaultMessage="Apply Referencing"
            />
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
